import * as THREE from 'three';

export const PORTAL_W = 1.7;
export const PORTAL_H = 2.7;
export const PORTAL_HALF_W = PORTAL_W * 0.5;
export const PORTAL_HALF_H = PORTAL_H * 0.5;

const _v = new THREE.Vector3();

const SCREEN_VERT = /* glsl */`
  varying vec4 vClip;
  varying vec3 vLocal;
  void main() {
    vLocal = position;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vClip = projectionMatrix * mv;
    gl_Position = vClip;
  }
`;

const SCREEN_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D tView;
  uniform vec3  uColor;
  uniform float uTime;
  uniform float uOpen;      // 0..1 iris animation
  uniform float uHasView;   // 0 when recursion ran out
  uniform float uAspect;    // portal width / height
  varying vec4 vClip;
  varying vec3 vLocal;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }

  void main() {
    // Unit space of the portal face: the box geometry is 1x1x1 before scaling,
    // so xy in [-0.5, 0.5] maps to the full opening regardless of portal size.
    vec2 p = vLocal.xy * 2.0;
    float d = length(p);
    float open = uOpen;
    if (d > open) discard;

    float rim = smoothstep(open * 0.84, open, d);
    vec2 dir = d > 1e-4 ? p / d : vec2(0.0);

    // Screen space lookup so the view lines up with the real camera exactly.
    vec2 uv = (vClip.xy / vClip.w) * 0.5 + 0.5;

    // Refraction wobble concentrated at the edge of the iris.
    float wob = sin(uTime * 2.6 + d * 14.0) * 0.5 + 0.5;
    vec2 warp = dir * rim * rim * (0.004 + 0.010 * wob);
    vec2 ca = dir * rim * 0.006;

    vec3 col;
    if (uHasView > 0.5) {
      col.r = texture2D(tView, clamp(uv + warp + ca, 0.001, 0.999)).r;
      col.g = texture2D(tView, clamp(uv + warp,      0.001, 0.999)).g;
      col.b = texture2D(tView, clamp(uv + warp - ca, 0.001, 0.999)).b;
    } else {
      // Recursion floor: a receding tunnel rather than a hard flat colour.
      float t = pow(1.0 - d / max(open, 0.001), 2.0);
      col = uColor * (0.06 + 0.5 * t) * (0.6 + 0.4 * sin(d * 26.0 - uTime * 3.0));
      col += uColor * 0.35 * pow(t, 6.0);
    }

    // Energy sheet: rim glow, a travelling scan, and a little static.
    col += uColor * pow(rim, 2.0) * 0.9;
    // A single band sweeping the iris, not a fixed stack of stripes.
    float sweep = fract(vLocal.y + 0.5 - uTime * 0.35);
    col += uColor * smoothstep(0.86, 1.0, sweep) * 0.16 * (1.0 - rim);
    col += uColor * hash(floor(uv * 420.0) + floor(uTime * 24.0)) * 0.035;

    // The iris flashes white as it snaps open.
    col += vec3(1.0) * pow(1.0 - open, 2.0) * 1.6;

    gl_FragColor = vec4(col, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

let _uid = 0;

/**
 * One end of a portal pair. Owns its visuals only; placement rules, teleport
 * detection and view rendering all live in PortalSystem.
 */
export class Portal {
  constructor({ color = 0x2ff3ff, label = 'A' } = {}) {
    this.id = `portal_${label}_${_uid++}`;
    this.label = label;
    this.color = new THREE.Color(color);

    this.active = false;
    this.link = null;
    /** Host collider and the face rect the portal was clamped into. */
    this.host = null;
    this.face = null;

    this.group = new THREE.Group();
    this.group.matrixAutoUpdate = true;
    this.group.visible = false;

    this.normal = new THREE.Vector3(0, 0, 1);
    this.right = new THREE.Vector3(1, 0, 0);
    this.up = new THREE.Vector3(0, 1, 0);

    this.openAmount = 0;
    this._openTarget = 0;

    this._build();
  }

  _build() {
    // --- view surface: a thin prism, not a plane, so the camera near plane can
    // slice into it without ever revealing the wall behind.
    const geo = new THREE.BoxGeometry(1, 1, 1);
    this.screenMat = new THREE.ShaderMaterial({
      uniforms: {
        tView:    { value: null },
        uColor:   { value: this.color.clone() },
        uTime:    { value: 0 },
        uOpen:    { value: 0 },
        uHasView: { value: 0 },
        uAspect:  { value: PORTAL_W / PORTAL_H },
      },
      vertexShader: SCREEN_VERT,
      fragmentShader: SCREEN_FRAG,
      side: THREE.DoubleSide,
      transparent: false,
      depthWrite: true,
      depthTest: true,
      toneMapped: false,
    });
    this.screen = new THREE.Mesh(geo, this.screenMat);
    this.screen.frustumCulled = false;
    this.screen.renderOrder = 2;
    this.group.add(this.screen);

    // --- rim: an emissive ellipse that reads instantly at a distance
    const ring = new THREE.TorusGeometry(1, 0.055, 8, 72);
    this.ringMat = new THREE.MeshBasicMaterial({
      color: this.color.clone().multiplyScalar(1.05),
      toneMapped: false,
      transparent: true,
      opacity: 1,
    });
    this.ring = new THREE.Mesh(ring, this.ringMat);
    this.ring.scale.set(PORTAL_HALF_W, PORTAL_HALF_H, 1);
    this.ring.position.z = 0.03;
    this.ring.frustumCulled = false;
    this.group.add(this.ring);

    // --- outer halo, additive, gives the bloom pass something to chew on
    const halo = new THREE.RingGeometry(1.0, 1.24, 64, 1);
    this.haloMat = new THREE.MeshBasicMaterial({
      color: this.color.clone(),
      transparent: true,
      opacity: 0.16,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    this.halo = new THREE.Mesh(halo, this.haloMat);
    this.halo.scale.set(PORTAL_HALF_W, PORTAL_HALF_H, 1);
    this.halo.position.z = 0.02;
    this.halo.frustumCulled = false;
    this.group.add(this.halo);

    // --- rim sparks
    const COUNT = 84;
    const pos = new Float32Array(COUNT * 3);
    const seed = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      const a = (i / COUNT) * Math.PI * 2;
      pos[i * 3] = Math.cos(a) * PORTAL_HALF_W;
      pos[i * 3 + 1] = Math.sin(a) * PORTAL_HALF_H;
      pos[i * 3 + 2] = 0.02;
      seed[i] = Math.random();
    }
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    pg.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    this.sparkMat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: this.color.clone() },
        uTime: { value: 0 },
        uOpen: { value: 0 },
        uScale: { value: 1 },
      },
      vertexShader: /* glsl */`
        attribute float aSeed;
        uniform float uTime; uniform float uOpen; uniform float uScale;
        varying float vA;
        void main() {
          float t = uTime * (0.5 + aSeed * 1.4) + aSeed * 31.4;
          vec3 p = position;
          float wob = sin(t * 2.0) * 0.09;
          p.xy *= 1.0 + wob;
          p.z += sin(t * 3.1) * 0.05;
          vA = (0.35 + 0.65 * (sin(t * 4.0) * 0.5 + 0.5)) * uOpen;
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_PointSize = (2.0 + 5.0 * aSeed) * uScale / max(-mv.z, 0.5) * 12.0;
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */`
        precision mediump float;
        uniform vec3 uColor; varying float vA;
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float d = 1.0 - smoothstep(0.1, 0.5, length(c));
          if (d <= 0.0) discard;
          gl_FragColor = vec4(uColor * d * 2.0, d * vA);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.sparks = new THREE.Points(pg, this.sparkMat);
    this.sparks.frustumCulled = false;
    this.group.add(this.sparks);
  }

  /**
   * Place the portal. `normal` points out of the surface; `up` is a hint that
   * gets orthogonalised against it.
   */
  placeAt(position, normal, upHint, host, face) {
    this.normal.copy(normal).normalize();
    this.up.copy(upHint);
    // Orthogonalise: remove any component of the hint along the normal.
    this.up.addScaledVector(this.normal, -this.up.dot(this.normal));
    if (this.up.lengthSq() < 1e-6) this.up.set(0, 1, 0).addScaledVector(this.normal, -this.normal.y);
    if (this.up.lengthSq() < 1e-6) this.up.set(1, 0, 0);
    this.up.normalize();
    this.right.crossVectors(this.up, this.normal).normalize();
    this.up.crossVectors(this.normal, this.right).normalize();

    const m = new THREE.Matrix4().makeBasis(this.right, this.up, this.normal);
    this.group.quaternion.setFromRotationMatrix(m);
    this.group.position.copy(position);
    this.group.updateMatrixWorld(true);

    this.host = host;
    this.face = face;
    this.active = true;
    this.group.visible = true;
    this.openAmount = 0.02;
    this._openTarget = 1;
    return this;
  }

  deactivate() {
    this.active = false;
    this._openTarget = 0;
    this.host = null;
    this.face = null;
  }

  /** Signed distance from the portal plane; positive is in front. */
  signedDistance(p) {
    return _v.subVectors(p, this.group.position).dot(this.normal);
  }

  /** Local face coordinates of a world point, as [u, v]. */
  localUV(p, out) {
    _v.subVectors(p, this.group.position);
    out.x = _v.dot(this.right);
    out.y = _v.dot(this.up);
    return out;
  }

  setView(texture) {
    this.screenMat.uniforms.tView.value = texture;
    this.screenMat.uniforms.uHasView.value = texture ? 1 : 0;
  }

  /**
   * Grow the prism so it always straddles the camera near plane. Without this
   * you can push your face into the portal and see through the wall.
   */
  fitToCamera(camera) {
    const halfH = camera.near * Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5);
    const halfW = halfH * camera.aspect;
    const thickness = THREE.MathUtils.clamp(
      Math.sqrt(halfW * halfW + halfH * halfH + camera.near * camera.near) * 2.2, 0.08, 0.5);
    this.screen.scale.set(PORTAL_W, PORTAL_H, thickness);
    this.screen.position.z = 0.012 + thickness * 0.5;
  }

  update(dt, time, cameraDistance) {
    const speed = this._openTarget > this.openAmount ? 7.5 : 9.0;
    this.openAmount += (this._openTarget - this.openAmount) * Math.min(1, dt * speed);
    if (this._openTarget === 0 && this.openAmount < 0.02) {
      this.openAmount = 0;
      this.group.visible = false;
    }
    const o = this.openAmount;
    this.screenMat.uniforms.uOpen.value = o;
    this.screenMat.uniforms.uTime.value = time;
    this.sparkMat.uniforms.uOpen.value = o;
    this.sparkMat.uniforms.uTime.value = time;
    this.ring.scale.set(PORTAL_HALF_W * o, PORTAL_HALF_H * o, 1);
    this.halo.scale.set(PORTAL_HALF_W * o, PORTAL_HALF_H * o, 1);
    this.ringMat.opacity = o;
    this.haloMat.opacity = 0.15 * o * (0.75 + 0.25 * Math.sin(time * 3.1));
    // Halo fades in close so it does not wash out the view surface.
    if (cameraDistance != null) {
      this.haloMat.opacity *= THREE.MathUtils.smoothstep(cameraDistance, 1.2, 4.0) * 0.85 + 0.15;
    }
  }

  dispose() {
    this.group.traverse((o) => {
      o.geometry?.dispose?.();
      if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
      else o.material?.dispose?.();
    });
  }
}
